import { Box, Grid, Card, CardActionArea, CardContent, Typography } from '@material-ui/core';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import cs from 'classnames';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { BiddingStatus, PreviewBiddingDataFragment } from '../../../../../api';
import useCommonStyles from '../../../../../useCommonStyles';

export type BiddingSessionsItemProps = {
    bidding: PreviewBiddingDataFragment;
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        infoContainer: {
            display: 'flex',
        },
        cardActionFocusHighlight: {
            backgroundColor: '#fff',
        },
        sessionImage: {
            borderRadius: '50%',
            background: theme.palette.secondary.main,
            textAlign: 'center',
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            width: '40px',
            height: '40px',
            fontSize: '18px',
            marginRight: theme.spacing(1),
            marginTop: '3px',
            marginLeft: '3px',
        },
        container: {
            display: 'flex',
        },
        title: {
            marginBottom: '10px',
        },
        description: {
            fontSize: '14px',
            color: '#717073',
        },
        content: {
            padding: 0,
        },
        headerContent: {
            borderBottom: `solid 1px ${theme.palette.primary}`,
        },
        body: {
            padding: theme.spacing(2),
            backgroundColor: '#EDEDED',
            color: '#717073',
            '& .MuiGrid-item:last-child': {
                opacity: '0.6',
            },
        },
        stage: {
            paddingRight: theme.spacing(1),
            paddingLeft: theme.spacing(1),
            marginLeft: theme.spacing(1),
            borderRadius: theme.spacing(0.5),
        },
        upcomingStage: {
            backgroundColor: theme.palette.secondary.main,
        },
        pastStage: {
            backgroundColor: '#717073',
        },
        currentStage: {
            backgroundColor: '#60C093',
        },
        status: {
            marginTop: theme.spacing(3),
            marginBottom: theme.spacing(3),
        },
        dateWrapper: {
            borderTop: `1px solid #C4C4C4`,
            paddingTop: '19px',
            marginTop: '10px',
        },
        dateContainer: {
            display: 'flex',
            flexDirection: 'column',
        },
        dateTime: {
            display: 'flex',
            justifyContent: 'space-between',
            flex: 1,
            marginBottom: '6px',
        },
        label: {
            marginRight: theme.spacing(1),
            fontSize: '14px',
        },
    })
);

const BiddingSessionsItem = ({ bidding }: BiddingSessionsItemProps) => {
    const { t } = useTranslation(['common', 'biddingSessionsPage']);
    const commonStyles = useCommonStyles();
    const styles = useStyles();
    const history = useHistory();

    const getStatusText = useCallback(() => {
        // for different status text
        switch (bidding.status) {
            case BiddingStatus.Current:
                return (
                    <Typography className={cs(commonStyles.transactionStatus, styles.currentStage)}>
                        {t('biddingSessionsPage:status.current')}
                    </Typography>
                );

            case BiddingStatus.Past:
                return (
                    <Typography className={cs(commonStyles.transactionStatus, styles.pastStage)}>
                        {t('biddingSessionsPage:status.past')}
                    </Typography>
                );

            case BiddingStatus.Upcoming:
                return (
                    <Typography className={cs(commonStyles.transactionStatus, styles.upcomingStage)}>
                        {t('biddingSessionsPage:status.upcoming')}
                    </Typography>
                );

            case BiddingStatus.Cancelled:
                return (
                    <Typography className={cs(commonStyles.transactionStatus, styles.pastStage)}>
                        {t('biddingSessionsPage:status.cancelled')}
                    </Typography>
                );

            default:
                return null;
        }
    }, [bidding, commonStyles, styles, t]);

    return (
        <Grid lg={3} md={6} sm={12} xs={12} item>
            <Card elevation={0}>
                <CardActionArea
                    classes={{ focusHighlight: styles.cardActionFocusHighlight }}
                    onClick={() => history.push(`/biddingSession/${bidding.id}`)}
                >
                    <CardContent>
                        <Box>
                            <Grid className={styles.headerContent} spacing={1} container>
                                <Grid container>
                                    <Grid className={styles.infoContainer} xs={11} item>
                                        <Box className={styles.sessionImage}>
                                            <Typography> {bidding.sessionName.charAt(0)}</Typography>
                                        </Box>
                                        <Box mt="10px">
                                            <Typography
                                                className={cs(
                                                    commonStyles.primary,
                                                    commonStyles.fontWeightBold,
                                                    styles.title
                                                )}
                                            >
                                                {bidding.sessionName}
                                            </Typography>
                                            <Typography className={styles.description}>{bidding.notes}</Typography>
                                            <Box
                                                className={cs(commonStyles.smallFontSize, styles.status)}
                                                display="flex"
                                            >
                                                {getStatusText()}
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid xs={1} item>
                                        <ChevronRightIcon color="secondary" />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid className={styles.dateWrapper} container>
                                <Grid className={styles.dateContainer} container>
                                    <Box className={styles.dateTime}>
                                        <Typography className={styles.label} color="secondary">
                                            {t('biddingSessionsPage:list.startTimeDate')}
                                        </Typography>
                                        <Typography className={styles.label} color="secondary">
                                            {t('common:formats.longDateTime', { date: new Date(bidding.startPeriod) })}
                                        </Typography>
                                    </Box>

                                    <Box className={styles.dateTime}>
                                        <Typography className={styles.label} color="secondary">
                                            {t('biddingSessionsPage:list.endTimeDate')}
                                        </Typography>
                                        <Typography className={styles.label} color="secondary">
                                            {t('common:formats.longDateTime', { date: new Date(bidding.endPeriod) })}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </CardContent>
                </CardActionArea>
            </Card>
        </Grid>
    );
};

export default BiddingSessionsItem;
